import React, { useEffect } from 'react';
import './ConfirmDialog.css';

/**
 * In-app confirmation modal — replaces native window.confirm() so destructive
 * and state-changing actions use a branded, accessible dialog (PRD-1119).
 *
 * Prefer driving this through the useConfirm() hook, which hands back a
 * promise so call sites read almost like the old window.confirm().
 */
function ConfirmDialog({
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default', // 'default' | 'danger'
  busy = false,
  onConfirm,
  onCancel,
}) {
  // While this dialog is up it OWNS Escape, and says so by stopping the event.
  //
  // It is the topmost layer, so a host still listening on `document` underneath
  // must not also react. In document review that host's handler called
  // confirm() again, which replaced the single dialog slot — the first promise
  // never settled, so the close it was gating could never happen.
  //
  // Capture phase for the same reason as RowActionsMenu: `document` is the
  // first hop of the capture path, so this runs before any host's bubble
  // listener and stopping there prevents the rest of the path. Registration
  // order cannot be relied on — this mounts long after the hosts do.
  //
  // Escape cancels rather than confirms: the dialog exists to guard something
  // destructive, and the safe answer is the one a stray keypress should give.
  useEffect(() => {
    if (busy) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [busy, onCancel]);

  return (
    <div className="confirm-dialog-overlay" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="confirm-dialog"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        <h2 className="confirm-dialog-title">{title}</h2>
        {message && <p className="confirm-dialog-message">{message}</p>}
        <div className="confirm-dialog-actions">
          <button type="button" className="confirm-dialog-cancel" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-dialog-confirm ${variant === 'danger' ? 'confirm-dialog-confirm--danger' : ''}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
